import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isCancel } from "axios";
import { fetchProductsHistoryReport } from "../api/fetchProductsHistoryReport/fetchProductsHistoryReportApi";
import {
  FetchProductsHistoryReportParams,
  ProductsHistoryReportItem,
} from "../api/fetchProductsHistoryReport/types";
import { AppDispatch, AppThunk } from "@/app/store";

interface ProductsHistoryReportState {
  items: ProductsHistoryReportItem[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: ProductsHistoryReportState = {
  items: [],
  totalCount: 0,
  loading: false,
  error: null,
};

const productsHistoryReportSlice = createSlice({
  name: "productsHistoryReport",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setReport: (
      state,
      action: PayloadAction<{
        items: ProductsHistoryReportItem[];
        totalCount: number;
      }>,
    ) => {
      state.items = action.payload.items;
      state.totalCount = action.payload.totalCount;
    },
  },
});

export const { setLoading, setError, setReport } =
  productsHistoryReportSlice.actions;

export const fetchProductsHistoryReportAction =
  (
    params: FetchProductsHistoryReportParams = {},
    signal?: AbortSignal,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await fetchProductsHistoryReport(params, signal);
      dispatch(
        setReport({
          items: response.data,
          totalCount: response.meta?.totalCount ?? 0,
        }),
      );
    } catch (error) {
      // A cancelled request means a newer one is in flight - leave the
      // loading flag on and let the replacement request settle the state.
      if (isCancel(error)) return;
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to fetch products history report",
        ),
      );
    } finally {
      if (!signal?.aborted) {
        dispatch(setLoading(false));
      }
    }
  };

export default productsHistoryReportSlice.reducer;
